import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Divider,
} from "@mui/material";
import { PortableText } from "@portabletext/react";
import { getPostBySlug } from "../../services/sanityApi";
import { CustomOutlinedButton } from "../../components/OutlinedButton";

const ArticolPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    getPostBySlug(slug)
      .then(setArticle)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: 800,
          mx: "auto",
          pt: { xs: 10, sm: 14 },
          pb: { xs: 6, sm: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <CustomOutlinedButton
          onClick={() => navigate(-1)}
          text="← Înapoi"
          sx={{
            height: "36px !important",
            fontSize: "0.8rem",
            mb: 4,
          }}
        />

        {error && (
          <Alert severity="error" sx={{ fontFamily: "Inter" }}>
            {error}
          </Alert>
        )}

        {loading && (
          <>
            <Skeleton height={40} width="70%" sx={{ mb: 1 }} />
            <Skeleton height={24} width="40%" sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" height={400} sx={{ mb: 3, borderRadius: 2 }} />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} height={20} width={i % 3 === 2 ? "60%" : "100%"} sx={{ mb: 1 }} />
            ))}
          </>
        )}

        {!loading && article && (
          <>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: "Libre Baskerville",
                fontWeight: 700,
                mb: 1.5,
                lineHeight: 1.3,
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              }}
            >
              {article.title}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: "Inter", color: "#800020" }}
              >
                {article.date}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "Inter", color: "#888" }}
              >
                de {article.authorName}
              </Typography>
            </Box>

            {article.featuredImage && (
              <Box
                component="img"
                src={article.featuredImage}
                alt={article.title}
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  mb: 4,
                  maxHeight: 480,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            )}

            <Divider sx={{ mb: 4, borderColor: "#f0ece4" }} />

            <Box
              sx={{
                "& p": {
                  fontFamily: "Inter",
                  lineHeight: 1.8,
                  mb: 2,
                  color: "#333",
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                },
                "& h2, & h3, & h4": {
                  fontFamily: "Libre Baskerville",
                  fontWeight: 700,
                  mt: 3,
                  mb: 1.5,
                  color: "#1a1a1a",
                },
                "& img": {
                  maxWidth: "100%",
                  borderRadius: "6px",
                  my: 2,
                },
                "& blockquote": {
                  borderLeft: "4px solid #800020",
                  pl: 3,
                  ml: 0,
                  my: 3,
                  fontStyle: "italic",
                  color: "#555",
                },
                "& a": {
                  color: "#800020",
                  textDecoration: "underline",
                },
                "& ul, & ol": {
                  pl: 3,
                  "& li": {
                    fontFamily: "Inter",
                    mb: 0.5,
                    lineHeight: 1.7,
                    color: "#333",
                  },
                },
              }}
            >
              {Array.isArray(article.body) && (
                <PortableText value={article.body} />
              )}
            </Box>
          </>
        )}
      </Box>

    </Box>
  );
};

export default ArticolPage;
